import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from '@/components/common/Skeleton/Skeleton';
import { WebBodyText14 } from '@fintronners/react-ui/src/GlobalStyling/webTypography';
import { thousandFormatNumbroCurrency } from '@fintronners/react-utils/src/numberUtilsTSX';
import type { FixedIncomeTradeBreakdown as FixedIncomeTradeBreakdownType } from '@/hooks/trade/useFixedIncomeTradeBreakdown';
import { usePreviewOrderCostStore } from '@/stores/trades/preview-order-cost';
import { usePreviewTradeFeeStore } from '@/stores/trades/preview-trade-fee';

const SmallTextSkeleton = () => <Skeleton width={60} height={14} />;
const LargeTextSkeleton = () => <Skeleton width={100} height={14} />;

type FixedIncomeTradeBreakdownProps = {
  breakdown: FixedIncomeTradeBreakdownType;
};

const FixedIncomeTradeBreakdown = ({ breakdown }: FixedIncomeTradeBreakdownProps) => {
  const { t } = useTranslation();

  const isPreviewOrderCostLoading = usePreviewOrderCostStore((state) => state.loading);
  const isPreviewTradeFeeLoading = usePreviewTradeFeeStore((state) => state.loading);
  const isLoading = isPreviewOrderCostLoading || isPreviewTradeFeeLoading;
  const hasNoData = breakdown.grossOrderAmount === 0;

  return (
    <div className="bg-grey7 p-3 space-y-1 rounded-lg">
      <div className="flex items-center justify-between">
        <WebBodyText14.Regular className="text-grey72">
          {t('miscScreens.tradeOptions.fixedIncome.tradeBreakdown.faceValue')}:
        </WebBodyText14.Regular>

        <WebBodyText14.Bold className="text-darkestBlue">
          {breakdown.faceValue === undefined
            ? '-'
            : thousandFormatNumbroCurrency(breakdown.faceValue)}
        </WebBodyText14.Bold>
      </div>

      <div className="flex items-center justify-between">
        <WebBodyText14.Regular className="text-grey72">
          {t('miscScreens.tradeOptions.fixedIncome.tradeBreakdown.accruedInterest')}:
        </WebBodyText14.Regular>

        <WebBodyText14.Bold className="text-darkestBlue">
          {isLoading ? (
            <SmallTextSkeleton />
          ) : hasNoData ? (
            '-'
          ) : (
            thousandFormatNumbroCurrency(
              breakdown.accruedInterestPerBond,
              undefined,
              thousandFormatNumbroCurrency(0),
            )
          )}
        </WebBodyText14.Bold>
      </div>

      <div className="flex items-center justify-between">
        <WebBodyText14.Regular className="text-grey72">
          {t('miscScreens.tradeOptions.fixedIncome.tradeBreakdown.markup')}:
        </WebBodyText14.Regular>

        <WebBodyText14.Bold className="text-darkestBlue">
          {isLoading ? (
            <SmallTextSkeleton />
          ) : hasNoData ? (
            '-'
          ) : (
            thousandFormatNumbroCurrency(breakdown.markup)
          )}
        </WebBodyText14.Bold>
      </div>

      <div className="flex items-center justify-between">
        <WebBodyText14.Regular className="text-grey72">
          {t('miscScreens.tradeOptions.fixedIncome.tradeBreakdown.commission')}:
        </WebBodyText14.Regular>

        <WebBodyText14.Bold className="text-darkestBlue">
          {isLoading ? (
            <SmallTextSkeleton />
          ) : hasNoData ? (
            '-'
          ) : (
            thousandFormatNumbroCurrency(breakdown.commission)
          )}
        </WebBodyText14.Bold>
      </div>

      <div className="flex items-center justify-between">
        <WebBodyText14.Regular className="text-grey72">
          {t('miscScreens.tradeOptions.fixedIncome.tradeBreakdown.timeInForce')}:
        </WebBodyText14.Regular>

        <WebBodyText14.Bold className="text-darkestBlue">
          {t('miscScreens.tradeOptions.fixedIncome.tradeBreakdown.timeInForceValue', {
            count: 1,
          })}
        </WebBodyText14.Bold>
      </div>

      <div className="flex items-center justify-between">
        <WebBodyText14.Regular className="text-grey72">
          {t('miscScreens.tradeOptions.fixedIncome.tradeBreakdown.orderValue_estimated')}:
        </WebBodyText14.Regular>

        <WebBodyText14.Bold className="text-darkestBlue">
          {isLoading ? (
            <LargeTextSkeleton />
          ) : hasNoData ? (
            '-'
          ) : (
            thousandFormatNumbroCurrency(breakdown.netOrderAmount)
          )}
        </WebBodyText14.Bold>
      </div>
    </div>
  );
};

export default memo(FixedIncomeTradeBreakdown);
